import React, { useEffect, useState } from "react";
import { Box, Collapse, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FacebookOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router";

interface SubItem {
  title: string;
  link: string;
}

const SUB_ITEMS: SubItem[] = [
  { title: "Dashboard", link: "/Facebook/Dashboard" },
  { title: "Ads", link: "/Facebook/Ads" },
  { title: "Templates", link: "/Facebook/Templates" },
  { title: "Leads", link: "/Facebook/Leads" },
  // { title: "FB Chatbot", link: "/Facebook/FBChatbot" },
  { title: "Settings", link: "/Facebook/Settings" },
];

const isSubActive = (link: string, pathname: string) =>
  pathname === link || pathname.startsWith(link + "/");

export default function FacebookMenu({
  openSidebar,
  handleCloseSidebar,
}: {
  openSidebar: boolean;
  handleCloseSidebar?: () => void;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const sectionActive = pathname.startsWith("/Facebook");
  const [expanded, setExpanded] = useState(sectionActive);

  // Open the accordion automatically when navigating into the section.
  useEffect(() => {
    if (sectionActive) setExpanded(true);
  }, [sectionActive]);

  const handleHeaderClick = () => {
    // Toggle the accordion in both expanded and collapsed sidebar modes.
    setExpanded((e) => !e);
  };

  const goTo = (link: string) => {
    navigate(link);
    if (handleCloseSidebar) handleCloseSidebar();
  };

  return (
    <Box>
      <Tooltip title="Facebook" placement="right" disableHoverListener={openSidebar}>
        <Box
          onClick={handleHeaderClick}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexDirection: "row",
            color: "white",
            padding: "8px",
            backgroundColor: sectionActive && !expanded ? "#2962FF" : "transparent",
            borderRadius: "8px",
            justifyContent: openSidebar ? "flex-start" : "center",
            "&:hover": {
              backgroundColor: sectionActive && !expanded ? "#2962FF" : "rgba(255,255,255,0.06)",
            },
          }}
        >
          <FacebookOutlined sx={{ width: "20px", height: "20px" }} />
          {openSidebar && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontSize: "14px", fontWeight: "500", color: "white" }}
              >
                Facebook
              </Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: "20px",
                  color: "white",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                }}
              />
            </Box>
          )}
        </Box>
      </Tooltip>

      {openSidebar && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              ml: "18px",
              pl: "10px",
              mt: "2px",
              borderLeft: "1px solid #364153",
              display: "flex",
              flexDirection: "column",
              // gap: "2px",
            }}
          >
            {SUB_ITEMS.map((sub) => {
              const active = isSubActive(sub.link, pathname);
              return (
                <Box
                  key={sub.link}
                  onClick={() => goTo(sub.link)}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    backgroundColor: active ? "#2962FF" : "transparent",
                    "&:hover": { backgroundColor: active ? "#2962FF" : "rgba(255,255,255,0.06)" },
                  }}
                >
                  <Box
                    sx={{
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      backgroundColor: active ? "white" : "#99A1AF",
                      flex: "0 0 5px",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: active ? 600 : 500,
                      color: active ? "white" : "#C9CEE8",
                    }}
                  >
                    {sub.title}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      )}

      {!openSidebar && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: "2px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "2px",
            }}
          >
            {SUB_ITEMS.map((sub) => {
              const active = isSubActive(sub.link, pathname);
              return (
                <Tooltip key={sub.link} title={sub.title} placement="right">
                  <Box
                    onClick={() => goTo(sub.link)}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: active ? "#2962FF" : "transparent",
                      "&:hover": {
                        backgroundColor: active ? "#2962FF" : "rgba(255,255,255,0.06)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: active ? 600 : 500,
                        color: active ? "white" : "#C9CEE8",
                      }}
                    >
                      {sub.title.charAt(0)}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
